/**
 * SurfaceSetup.js
 *
 * Compound design option for boxed elements (buttons, cards, badges, etc.):
 * background + hover, border (style/width/color), border-radius, margin
 * and padding — all gated behind one shared "{name}Option" default/custom
 * toggle, same architecture as TypographySetup (local display state as the
 * render source of truth, synced to the store as a side effect on change).
 *
 * Storage convention (flat fields, so the existing style:{selector,property}
 * renderer needs no changes):
 *   {name}Bg
 *   {name}BgHover
 *   {name}BorderStyle
 *   {name}BorderWidth
 *   {name}BorderColor
 *   {name}BorderColorHover
 *   {name}BorderRadius
 *   {name}Margin            <- 4-value shorthand string, e.g. "8px 8px 8px 8px"
 *   {name}MarginLinked
 *   {name}Padding            <- same shorthand + Linked convention
 *   {name}PaddingLinked
 *   {name}Option            <- single "default" | "custom" toggle for the group
 */

import React, { useCallback, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";

import ColorSelector from "./ColorSelector";
import CssBoxInput from "./CssBoxInput";
import { CSSUnitSelector } from "./CSSUnitSelector";
import DesignTooltip from "./DesignTooltip";
import { BuilderInputSetupPropTypes } from "./BuilderOptionPropTypes";
import { CSSPositions, CSSUnits } from "../../../../../builder/consts";
import WidgetSettingsContext from "../../../../../contexts/WidgetSettingsContext";

const DEFAULT_BORDER_STYLE_OPTIONS = [
  { label: "None", value: "none" },
  { label: "Solid", value: "solid" },
  { label: "Dashed", value: "dashed" },
  { label: "Dotted", value: "dotted" },
];

const BOX_KEYS = ["margin", "padding"];

const subField = (name, suffix) => `${name}${suffix}`;

/**
 * Splits a raw CSS value like "14px" into its numeric part and unit,
 * falling back to the first known unit if none is present/recognized.
 */
const parseDimension = (raw, fallbackUnit = CSSUnits[0]) => {
  const match = String(raw ?? "").match(/(-?\d*\.?\d+)([a-zA-Z%]*)/);

  if (!match) {
    return { value: "", unit: fallbackUnit };
  }

  const [, value, unit] = match;
  return { value, unit: CSSUnits.includes(unit) ? unit : fallbackUnit };
};

/**
 * Splits a 4-value shorthand string ("8px 8px 8px 8px") into per-side
 * values sharing one unit (the unit of the first side found) — a box only
 * ever edits with one unit at a time here, same as CssBox.js.
 */
const parseBoxShorthand = (raw, fallbackUnit = CSSUnits[0]) => {
  const parts = String(raw ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  const parsed = parts.map((p) => parseDimension(p, fallbackUnit));
  const unit = parsed[0] ? parsed[0].unit : fallbackUnit;
  const [top, right, bottom, left] = parsed;
  const fallback = top || { value: "" };

  return {
    unit,
    values: {
      top: fallback.value,
      right: (right || fallback).value,
      bottom: (bottom || fallback).value,
      left: (left || fallback).value,
    },
  };
};

const buildBoxShorthand = (values, unit) =>
  `${values.top}${unit} ${values.right}${unit} ${values.bottom}${unit} ${values.left}${unit}`;

const SurfaceSetup = (props) => {
  const {
    title,
    name,
    defaultValue = {},
    control,
    settingsName,
    updateDesignProp,
  } = props;

  const {
    showBg = true,
    showBgHover = false,
    showBorderStyle = false,
    showBorderWidth = false,
    showBorderColor = false,
    showBorderColorHover = false,
    showBorderRadius = false,
    showMargin = false,
    showPadding = false,
    borderStyleOptions = DEFAULT_BORDER_STYLE_OPTIONS,
  } = control.options || {};

  const nameOption = `${name}Option`;
  const inputName = `${settingsName}-surface-setup-${name}`;

  const widgetSettingsContext = useContext(WidgetSettingsContext);
  const optionValue = widgetSettingsContext[nameOption] || "default";
  const isCustom = optionValue === "custom";

  const fields = {
    bg: subField(name, "Bg"),
    bgHover: subField(name, "BgHover"),
    borderStyle: subField(name, "BorderStyle"),
    borderWidth: subField(name, "BorderWidth"),
    borderColor: subField(name, "BorderColor"),
    borderColorHover: subField(name, "BorderColorHover"),
    borderRadius: subField(name, "BorderRadius"),
    margin: subField(name, "Margin"),
    padding: subField(name, "Padding"),
  };

  const buildDisplayValues = useCallback(
    () =>
      Object.entries(fields).reduce((acc, [key, fieldName]) => {
        acc[key] = widgetSettingsContext[fieldName] ?? defaultValue[key] ?? "";
        return acc;
      }, {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [widgetSettingsContext, name]
  );

  // Same rationale as TypographySetup: local state is the render source of
  // truth, not WidgetSettingsContext directly, so typing/clicking reflects
  // instantly regardless of the store round-trip timing.
  const [displayValues, setDisplayValues] = useState(buildDisplayValues);

  const buildUnits = useCallback(
    () => ({
      borderWidth: parseDimension(
        widgetSettingsContext[fields.borderWidth] ?? defaultValue.borderWidth
      ).unit,
      borderRadius: parseDimension(
        widgetSettingsContext[fields.borderRadius] ?? defaultValue.borderRadius
      ).unit,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [widgetSettingsContext, name]
  );

  const [units, setUnits] = useState(buildUnits);

  const buildBoxState = useCallback(
    () =>
      BOX_KEYS.reduce(
        (acc, key) => {
          const raw = widgetSettingsContext[fields[key]] ?? defaultValue[key];
          const { unit, values } = parseBoxShorthand(raw);
          acc.units = { ...acc.units, [key]: unit };
          acc.values = { ...acc.values, [key]: values };
          acc.linked = {
            ...acc.linked,
            [key]: widgetSettingsContext[fields[key] + "Linked"] ?? true,
          };
          return acc;
        },
        { units: {}, values: {}, linked: {} }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [widgetSettingsContext, name]
  );

  const [boxState, setBoxState] = useState(buildBoxState);

  // Deliberately NOT seeded from optionValue: even a calendar that already
  // has custom surface settings saved should load collapsed, so a busy
  // custom config doesn't dump a wall of fields into view unprompted. It
  // only auto-opens as an immediate reaction to the user flipping the
  // radio to Custom themselves (see onOptionChange below), and can be
  // toggled independently of that radio afterwards.
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setDisplayValues(buildDisplayValues());
    setUnits(buildUnits());
    setBoxState(buildBoxState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionValue, name]);

  /**
   * Flip the whole group between default/custom. Switching to custom
   * seeds every sub-field from defaultValue so nothing renders blank.
   */
  const onOptionChange = useCallback(
    (kind) => () => {
      const updates = [{ field: nameOption, value: kind }];

      if (kind === "custom") {
        Object.entries(fields).forEach(([key, fieldName]) => {
          updates.push({
            field: fieldName,
            value: widgetSettingsContext[fieldName] ?? defaultValue[key] ?? "",
          });
        });
      }

      // Custom -> immediately reveal the fields, since the user just asked
      // to customize something. Default -> nothing to show, so collapse.
      setExpanded(kind === "custom");
      updateDesignProp(updates);
    },
    [updateDesignProp, widgetSettingsContext, defaultValue]
  );

  const onDropdownChange = useCallback(
    (key, fieldName) => (e) => {
      const { value } = e.target;
      setDisplayValues((prev) => ({ ...prev, [key]: value }));
      updateDesignProp([{ field: fieldName, value }]);
    },
    [updateDesignProp]
  );

  const onColorChange = useCallback(
    (key, fieldName) => (color) => {
      const { r, g, b, a } = color.rgb;
      const alpha = Math.round(a * 255);
      const hex =
        "#" +
        r.toString(16).padStart(2, "0") +
        g.toString(16).padStart(2, "0") +
        b.toString(16).padStart(2, "0") +
        alpha.toString(16).padStart(2, "0");
      setDisplayValues((prev) => ({ ...prev, [key]: hex }));
      updateDesignProp([{ field: fieldName, value: hex }]);
    },
    [updateDesignProp]
  );

  const onDimensionValueChange = useCallback(
    (key, fieldName) => (rawValue) => {
      const combined = `${rawValue}${units[key]}`;
      setDisplayValues((prev) => ({ ...prev, [key]: combined }));
      updateDesignProp([{ field: fieldName, value: combined }]);
    },
    [updateDesignProp, units]
  );

  const onDimensionUnitChange = useCallback(
    (key, fieldName) => (unit) => {
      const { value } = parseDimension(displayValues[key], units[key]);
      const combined = `${value}${unit}`;
      setUnits((prev) => ({ ...prev, [key]: unit }));
      setDisplayValues((prev) => ({ ...prev, [key]: combined }));
      updateDesignProp([{ field: fieldName, value: combined }]);
    },
    [updateDesignProp, displayValues, units]
  );

  /**
   * position is undefined when called from the unit selector (applies to
   * all 4 sides at once); otherwise it's the single side that changed.
   */
  const onBoxValueChange = useCallback(
    (boxKey, fieldName) => (rawValue, position) => {
      setBoxState((prev) => {
        const linked = prev.linked[boxKey];
        const unit = prev.units[boxKey];
        const nextValues = linked
          ? { top: rawValue, right: rawValue, bottom: rawValue, left: rawValue }
          : { ...prev.values[boxKey], [position]: rawValue };

        const combined = buildBoxShorthand(nextValues, unit);
        setDisplayValues((dv) => ({ ...dv, [boxKey]: combined }));
        updateDesignProp([{ field: fieldName, value: combined }]);

        return {
          ...prev,
          values: { ...prev.values, [boxKey]: nextValues },
        };
      });
    },
    [updateDesignProp]
  );

  const onBoxUnitChange = useCallback(
    (boxKey, fieldName) => (unit) => {
      setBoxState((prev) => {
        const combined = buildBoxShorthand(prev.values[boxKey], unit);
        setDisplayValues((dv) => ({ ...dv, [boxKey]: combined }));
        updateDesignProp([{ field: fieldName, value: combined }]);

        return {
          ...prev,
          units: { ...prev.units, [boxKey]: unit },
        };
      });
    },
    [updateDesignProp]
  );

  const onBoxLinkToggle = useCallback(
    (boxKey, fieldName, linkedFieldName) => () => {
      setBoxState((prev) => {
        const nextLinked = !prev.linked[boxKey];
        const values = prev.values[boxKey];
        // collapsing to linked snaps all sides to the top value, same as
        // CssBox.js does when its own link toggle is switched on.
        const nextValues = nextLinked
          ? {
              top: values.top,
              right: values.top,
              bottom: values.top,
              left: values.top,
            }
          : values;

        const combined = buildBoxShorthand(nextValues, prev.units[boxKey]);
        setDisplayValues((dv) => ({ ...dv, [boxKey]: combined }));
        updateDesignProp([
          { field: linkedFieldName, value: nextLinked },
          { field: fieldName, value: combined },
        ]);

        return {
          ...prev,
          linked: { ...prev.linked, [boxKey]: nextLinked },
          values: { ...prev.values, [boxKey]: nextValues },
        };
      });
    },
    [updateDesignProp]
  );

  if (!props.show) {
    return null;
  }

  const dimensionRow = (label, key, fieldName, placeholder) => {
    const { value } = parseDimension(displayValues[key], units[key]);

    return (
      <div className="typography-row" key={fieldName}>
        <div className="d-flex align-items-center justify-content-between">
          <span className="typography-row-label">{label}</span>
          <CSSUnitSelector
            value={units[key]}
            setValue={onDimensionUnitChange(key, fieldName)}
            disabled={!isCustom}
          />
        </div>
        <CssBoxInput
          value={value}
          setValue={onDimensionValueChange(key, fieldName)}
          placeholder={placeholder}
          disabled={!isCustom}
        />
      </div>
    );
  };

  const dropdownRow = (label, key, fieldName, dropdownOptions) => (
    <div className="typography-row" key={fieldName}>
      <span className="typography-row-label">{label}</span>
      <select
        disabled={!isCustom}
        className="form-control"
        value={displayValues[key] ?? ""}
        onChange={onDropdownChange(key, fieldName)}
      >
        {dropdownOptions.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );

  const colorRow = (label, key, fieldName) => (
    <div className="typography-row typography-row-color" key={fieldName}>
      <span className="typography-row-label">{label}</span>
      <ColorSelector
        id={`${inputName}-${fieldName}`}
        disabled={!isCustom}
        color={displayValues[key] ?? ""}
        onColorChagnge={onColorChange(key, fieldName)}
      />
    </div>
  );

  /**
   * Places two rows side by side when both are present, falls back to a
   * single full-width row when only one is — same helper as
   * TypographySetup uses for its Color/Color-on-hover pair.
   */
  const renderPair = (a, b) => {
    if (a && b) {
      return (
        <div className="typography-row-pair">
          {a}
          {b}
        </div>
      );
    }
    return a || b || null;
  };

  const boxRow = (label, boxKey, fieldName) => {
    const linkedFieldName = `${fieldName}Linked`;
    const linked = boxState.linked[boxKey];
    const unit = boxState.units[boxKey];
    const values = boxState.values[boxKey];

    return (
      <div className="typography-row" key={fieldName}>
        <div className="d-flex align-items-center justify-content-between">
          <span className="typography-row-label">{label}</span>
          <CSSUnitSelector
            value={unit}
            setValue={onBoxUnitChange(boxKey, fieldName)}
            disabled={!isCustom}
          />
        </div>
        <div className="cssbox-container">
          {CSSPositions.map((position) => (
            <CssBoxInput
              key={`${fieldName}_${position}`}
              title={position.charAt(0).toUpperCase() + position.slice(1)}
              value={values[position]}
              setValue={onBoxValueChange(boxKey, fieldName)}
              position={position}
              inputClassName={"text-center"}
              disabled={!isCustom || (linked && position !== "top")}
            />
          ))}
          <div className={classnames("button-container", { linked })}>
            <button
              type="button"
              disabled={!isCustom}
              onClick={onBoxLinkToggle(boxKey, fieldName, linkedFieldName)}
              className={"d-flex"}
            >
              <i className="material-icons" aria-hidden="true">
                {linked ? "lock" : "lock_open"}
              </i>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const hasBackgroundFields = showBg || showBgHover;
  const hasBorderFields =
    showBorderStyle ||
    showBorderWidth ||
    showBorderColor ||
    showBorderColorHover ||
    showBorderRadius;
  const hasSpacingFields = showMargin || showPadding;

  const section = (label, content) =>
    content ? (
      <div className="surface-setup-group" key={label}>
        <span className="surface-setup-group-title">{label}</span>
        <div className="surface-setup-group-body">{content}</div>
      </div>
    ) : null;

  const expandToggle = isCustom && (
    <button
      type="button"
      className="surface-setup-expand-toggle"
      onClick={() => setExpanded((prev) => !prev)}
    >
      <i className="material-icons" aria-hidden="true">
        {expanded ? "expand_less" : "expand_more"}
      </i>{" "}
      {expanded ? "Hide custom settings" : "Show custom settings"}
    </button>
  );

  const fieldsBody = isCustom && expanded && (
    <div className="typography-setup-fields">
      {section(
        "Background",
        hasBackgroundFields && (
          <>
            {showBg && colorRow("Background", "bg", fields.bg)}
            {showBgHover &&
              colorRow("Background on hover", "bgHover", fields.bgHover)}
          </>
        )
      )}
      {section(
        "Border",
        hasBorderFields && (
          <>
            {showBorderStyle &&
              dropdownRow(
                "Border style",
                "borderStyle",
                fields.borderStyle,
                borderStyleOptions
              )}
            {showBorderWidth &&
              dimensionRow(
                "Border width",
                "borderWidth",
                fields.borderWidth,
                "1"
              )}
            {renderPair(
              showBorderColor &&
                colorRow("Border color", "borderColor", fields.borderColor),
              showBorderColorHover &&
                colorRow(
                  "Border color on hover",
                  "borderColorHover",
                  fields.borderColorHover
                )
            )}
            {showBorderRadius &&
              dimensionRow(
                "Border radius",
                "borderRadius",
                fields.borderRadius,
                "0"
              )}
          </>
        )
      )}
      {section(
        "Spacing",
        hasSpacingFields && (
          <>
            {showMargin && boxRow("Margin", "margin", fields.margin)}
            {showPadding && boxRow("Padding", "padding", fields.padding)}
          </>
        )
      )}
    </div>
  );

  return (
    <div className={"design-element"}>
      <label className="option-label">
        <span className={"option-title"}>{title}</span>
        <DesignTooltip help={props.tooltip} />
      </label>

      <div className="form-group typography-setup-row">
        <div className="radio">
          <label>
            <input
              type="radio"
              name={inputName}
              checked={optionValue === "default"}
              onChange={onOptionChange("default")}
            />{" "}
            Default
          </label>
        </div>
        <div className="radio">
          <label>
            <input
              type="radio"
              name={inputName}
              checked={isCustom}
              onChange={onOptionChange("custom")}
            />{" "}
            Custom
          </label>
        </div>
      </div>

      {expandToggle}
      {fieldsBody}
    </div>
  );
};

SurfaceSetup.propTypes = {
  ...BuilderInputSetupPropTypes,

  defaultValue: PropTypes.shape({
    bg: PropTypes.string,
    bgHover: PropTypes.string,
    borderStyle: PropTypes.string,
    borderWidth: PropTypes.string,
    borderColor: PropTypes.string,
    borderColorHover: PropTypes.string,
    borderRadius: PropTypes.string,
    margin: PropTypes.string,
    padding: PropTypes.string,
  }),

  control: PropTypes.shape({
    kind: PropTypes.oneOf(["surface"]).isRequired,
    options: PropTypes.shape({
      showBg: PropTypes.bool,
      showBgHover: PropTypes.bool,
      showBorderStyle: PropTypes.bool,
      showBorderWidth: PropTypes.bool,
      showBorderColor: PropTypes.bool,
      showBorderColorHover: PropTypes.bool,
      showBorderRadius: PropTypes.bool,
      showMargin: PropTypes.bool,
      showPadding: PropTypes.bool,
      borderStyleOptions: PropTypes.arrayOf(
        PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })
      ),
    }),
  }).isRequired,
};

SurfaceSetup.defaultProps = {
  control: {
    kind: "surface",
    options: {
      showBg: true,
    },
  },
};

export default SurfaceSetup;
